import { useEffect, useState } from "react";
import "../styles/ApplicationDetailDrawer.css";
import Skeleton from "../components/common/Skeleton";
import FadeIn from "../components/common/FadeIn";
import api from "../../shared/AxiosConfig";
import { tEnum } from "../../config/translations";
import { toast } from "react-toastify";

const FIELD_LABELS = {
    identityDocumentType: "Kimlik türü",
    tckn: "TC Kimlik Numarası",
    passportNumber: "Pasaport numarası",
    phoneNumber: "Telefon Numarası",
    firstName: "Ad",
    lastName: "Soyad",
    email: "E-Posta",
    city: "Şehir",
    countryCode: "Ülke Kodu",
    gender: "Cinsiyet",
};

const ERROR_TYPE_MAP = {
    "any.required": "zorunludur",
    "string.email": "geçerli bir e-posta olmalıdır",
    "string.pattern.base": "formatı hatalı",
};

export default function EnrollmentDetailDrawer({ open, onClose, data, loading }) {

    const [mounted, setMounted] = useState(false);
    const [visible, setVisible] = useState(false);

    const [editMode, setEditMode] = useState(false);

    const emptyForm = {
        firstName: "",
        lastName: "",
        identityDocumentType: "TCKN",
        tckn: "",
        passportNumber: "",
        gender: "",
        birthDate: "",
        nationality: "",
        email: "",
        city: "",
        address: "",
        countryCode: "+90",
        phoneNumber: "",
        profession: "",
        interestArea: "",
        adminNote: ""
    };

    const [form, setForm] = useState(emptyForm);
    const [originalForm, setOriginalForm] = useState(emptyForm);

    const detail = data;
    const isCreateMode = data === null;


    /* ---------- Drawer Animation ---------- */
    useEffect(() => {
        if (open) {
            setMounted(true);
            requestAnimationFrame(() =>
                requestAnimationFrame(() => setVisible(true))
            );
        } else {
            setVisible(false);
            setTimeout(() => setMounted(false), 380);
        }

        setEditMode(isCreateMode);
        setForm(emptyForm);
        setOriginalForm(emptyForm);

    }, [open, isCreateMode]);


    /* ---------- Form ---------- */
    useEffect(() => {

        // CREATE MODE → boş form
        if (isCreateMode) {
            setForm(emptyForm);
            setOriginalForm(emptyForm);
            setEditMode(true);
            return;
        }

        // DETAIL MODE
        if (!detail) return;

        const loaded = {
            firstName: detail.personal?.fullName?.split(" ")[0] || "",
            lastName: detail.personal?.fullName?.split(" ")[1] || "",
            identityDocumentType: detail.personal?.identityDocumentType || "TCKN",
            tckn: detail.personal?.tckn || "",
            passportNumber: detail.personal?.passportNumber || "",
            gender: detail.personal?.gender || "",
            birthDate: detail.personal?.birthDate || "",
            nationality: detail.personal?.nationality || "",

            email: detail.contact?.email || "",
            city: detail.contact?.city || "",
            address: detail.contact?.address || "",
            countryCode: detail.contact?.phone?.split(" ")?.[0] || "+90",
            phoneNumber: detail.contact?.phone?.split(" ")?.[1] || "",

            profession: detail.education?.profession || "",
            interestArea: detail.education?.interestArea || "",
            adminNote: detail.adminNote || ""
        };

        setForm(loaded);
        setOriginalForm(loaded);
        setEditMode(false);

    }, [detail, isCreateMode]);


    if (!mounted) return null;


    const formatDate = (d) =>
        d
            ? new Date(d).toLocaleDateString("tr-TR", {
                day: "2-digit",
                month: "long",
                year: "numeric"
            })
            : "-";


    const statusColor = {
        ACTIVE: "badge-approve",
        SUSPENDED: "badge-review",
        COMPLETED: "badge-completed",
        DROPPED: "badge-rejected"
    };


    /* ---------- SAVE ---------- */
    const handleSave = async () => {
        try {

            // SADECE DEĞİŞENLERİ ve BOŞ OLMAYANLARI gönder
            const payload = Object.entries(form).reduce((acc, [key, value]) => {
                const original = originalForm?.[key];

                if (value === "" || value === null || value === undefined)
                    return acc;

                if (value === original)
                    return acc;

                acc[key] = value;
                return acc;
            }, {});

            if (isCreateMode) {
                await api.post(`/enrollment`, payload);
                toast.success("Öğrenci kaydı oluşturuldu");
            } else {
                await api.patch(`/enrollment/${detail.publicId}`, payload);
                toast.success("Kayıt başarıyla güncellendi");
            }

            setEditMode(false);
            onClose(true);

        } catch (e) {
            const details = e?.response?.data?.details;

            if (Array.isArray(details) && details.length > 0) {

                details.forEach(err => {
                    const fieldName =
                        FIELD_LABELS[err.field] ||
                        err.context?.label ||
                        err.field;

                    const typeMsg =
                        ERROR_TYPE_MAP[err.type] ||
                        "geçersiz";

                    toast.error(`${fieldName} ${typeMsg}`);
                });

            } else {
                toast.error("İşlem sırasında bir hata oluştu");
            }
        }
    };


    const handleChange = (key, value) => {
        if (key === "identityDocumentType") {
            setForm(prev => ({
                ...prev,
                identityDocumentType: value,
                tckn: value === "TCKN" ? prev.tckn : "",
                passportNumber: value === "PASSPORT" ? prev.passportNumber : "",
            }));
            return;
        }
        setForm(prev => ({ ...prev, [key]: value }));
    };


    return (
        <>
            <div
                className={`drawer-overlay ${visible ? "show" : ""}`}
                onClick={() => onClose(false)}
            />

            <div className={`detail-drawer ${visible ? "open" : ""}`}>

                {/* ---------- HEADER ---------- */}
                <div className="drawer-header">
                    <h3>{isCreateMode ? "Yeni Öğrenci Kaydı" : "Öğrenci Detayı"}</h3>

                    <div style={{ display: "flex", gap: 10 }}>

                        {/* --- DETAIL MODE --- */}
                        {!isCreateMode && !editMode && (
                            <button
                                className="drawer-icon-btn"
                                onClick={() => setEditMode(true)}
                                title="Düzenle"
                            >
                                <i className="bi bi-pencil-square"></i>
                            </button>
                        )}

                        {!isCreateMode && editMode && (
                            <>
                                <button
                                    className="drawer-icon-btn"
                                    title="Kaydet"
                                    onClick={handleSave}
                                >
                                    <i className="bi bi-check2-circle"></i>
                                </button>

                                <button
                                    className="drawer-icon-btn"
                                    title="İptal"
                                    onClick={() => setEditMode(false)}
                                >
                                    <i className="bi bi-arrow-counterclockwise"></i>
                                </button>
                            </>
                        )}

                        {/* --- CREATE MODE --- */}
                        {isCreateMode && (
                            <button
                                className="drawer-icon-btn"
                                title="Kaydet"
                                onClick={handleSave}
                            >
                                <i className="bi bi-check2-circle"></i>
                            </button>
                        )}

                        <button className="drawer-close" onClick={() => onClose(false)}>×</button>
                    </div>
                </div>


                <div className="drawer-body">


                    {!isCreateMode && (
                        <FadeIn delay={0.05}>
                            <div className="drawer-section">
                                <div className="section-title">Durum</div>

                                {loading ? (
                                    <Skeleton height={40}/>
                                ) : (
                                    <>
                                        <span className={`status-badge ${statusColor[detail?.enrollment?.status]}`}>
                                            {tEnum("ENROLLMENT_STATUS", detail?.enrollment?.status)}
                                        </span>
                                        
                                    </>
                                )}
                            </div>
                        </FadeIn>
                    )}


                    {!isCreateMode && (
                        <FadeIn delay={0.3}>
                            <div className="drawer-section">
                                <div className="section-title">Tarihçe</div>

                                <div className="info-grid single-column">
                                    <div><strong>Kayıt:</strong> {formatDate(detail?.enrollment?.enrolledAt)}</div>

                                    {detail?.enrollment?.completedAt && (
                                        <div><strong>Mezuniyet:</strong> {formatDate(detail?.enrollment?.completedAt)}</div>
                                    )}

                                    {detail?.enrollment?.droppedAt && (
                                        <div><strong>Ayrılma:</strong> {formatDate(detail?.enrollment?.droppedAt)}</div>
                                    )}
                                </div>
                                {detail?.enrollment?.termName && (
                                            <div style={{ marginTop: 8 }}>
                                                <strong>Dönem:</strong> {detail.enrollment.termName}
                                            </div>
                                        )}
                            </div>
                        </FadeIn>
                    )}

                    {/* ---------- ADMIN NOTE ---------- */}
                    <FadeIn delay={0.25}>
                        <div className="drawer-section">
                            <div className="section-title">Yönetici Notu</div>

                            <textarea disabled={!editMode}
                                      value={form?.adminNote}
                                      onChange={e => handleChange("adminNote", e.target.value)}
                                      className="drawer-input"/>
                        </div>
                    </FadeIn>


                    {/* ---------- PERSONAL ---------- */}
                    <FadeIn delay={0.1}>
                        <div className="drawer-section">
                            <div className="section-title">Kişisel Bilgiler</div>

                            <div className="info-grid single-column">

                                <label>Ad</label>
                                <input disabled={!editMode}
                                       value={form?.firstName}
                                       onChange={e => handleChange("firstName", e.target.value)}
                                       className="drawer-input"/>

                                <label>Soyad</label>
                                <input disabled={!editMode}
                                       value={form?.lastName}
                                       onChange={e => handleChange("lastName", e.target.value)}
                                       className="drawer-input"/>

                                <label>Kimlik türü</label>
                                <select
                                    disabled={!editMode}
                                    value={form?.identityDocumentType || "TCKN"}
                                    onChange={e => handleChange("identityDocumentType", e.target.value)}
                                    className="drawer-input"
                                >
                                    <option value="TCKN">{tEnum("IDENTITY_DOCUMENT_TYPE", "TCKN")}</option>
                                    <option value="PASSPORT">{tEnum("IDENTITY_DOCUMENT_TYPE", "PASSPORT")}</option>
                                </select>

                                {form?.identityDocumentType === "PASSPORT" ? (
                                    <>
                                        <label>Pasaport no</label>
                                        <input
                                            disabled={!editMode}
                                            value={form?.passportNumber}
                                            onChange={e => handleChange("passportNumber", e.target.value.toUpperCase())}
                                            className="drawer-input"
                                            autoCapitalize="characters"
                                        />
                                    </>
                                ) : (
                                    <>
                                        <label>TC Kimlik No</label>
                                        <input
                                            disabled={!editMode}
                                            value={form?.tckn}
                                            onChange={e => handleChange("tckn", e.target.value)}
                                            className="drawer-input"
                                        />
                                    </>
                                )}

                                <label>Cinsiyet</label>
                                <select
                                    disabled={!editMode}
                                    value={form?.gender}
                                    onChange={e => handleChange("gender", e.target.value)}
                                    className="drawer-input"
                                >
                                    <option value="">Seçiniz</option>
                                    <option value="MALE">{tEnum("GENDER","MALE")}</option>
                                    <option value="FEMALE">{tEnum("GENDER","FEMALE")}</option>
                                </select>

                                <label>Uyruğu</label>
                                <input disabled={!editMode}
                                       value={form?.nationality}
                                       onChange={e => handleChange("nationality", e.target.value)}
                                       className="drawer-input"/>

                            </div>
                        </div>
                    </FadeIn>


                    {/* ---------- CONTACT ---------- */}
                    <FadeIn delay={0.15}>
                        <div className="drawer-section">
                            <div className="section-title">İletişim</div>

                            <div className="info-grid single-column">

                                <label>Email</label>
                                <input disabled={!editMode}
                                       value={form?.email}
                                       onChange={e => handleChange("email", e.target.value)}
                                       className="drawer-input"/>

                                <label>Telefon</label>
                                <div style={{ display:"flex", gap:8 }}>
                                    <input disabled={!editMode}
                                           value={form?.countryCode}
                                           onChange={e => handleChange("countryCode", e.target.value)}
                                           className="drawer-input"/>

                                    <input disabled={!editMode}
                                           value={form?.phoneNumber}
                                           onChange={e => handleChange("phoneNumber", e.target.value)}
                                           className="drawer-input"/>
                                </div>

                                <label>Şehir</label>
                                <input disabled={!editMode}
                                       value={form?.city}
                                       onChange={e => handleChange("city", e.target.value)}
                                       className="drawer-input"/>

                                <label>Adres</label>
                                <textarea disabled={!editMode}
                                          value={form?.address}
                                          onChange={e => handleChange("address", e.target.value)}
                                          className="drawer-input"/>
                            </div>
                        </div>
                    </FadeIn>


                    {/* ---------- EDUCATION ---------- */}
                    <FadeIn delay={0.2}>
                        <div className="drawer-section">
                            <div className="section-title">Eğitim & İlgi</div>

                            <div className="info-grid single-column">

                                <label>Meslek</label>
                                <input disabled={!editMode}
                                       value={form?.profession}
                                       onChange={e => handleChange("profession", e.target.value)}
                                       className="drawer-input"/>

                                <label>İlgi Alanı</label>
                                <select
                                    disabled={!editMode}
                                    value={form?.interestArea}
                                    onChange={e => handleChange("interestArea", e.target.value)}
                                    className="drawer-input"
                                >
                                    <option value="">Seçiniz</option>
                                    <option value="COACHING">{tEnum("INTEREST_AREA", "COACHING")}</option>
                                    <option value="MANAGEMENT">{tEnum("INTEREST_AREA", "MANAGEMENT")}</option>
                                    <option value="REFEREE">{tEnum("INTEREST_AREA", "REFEREE")}</option>
                                    <option value="PLAYER_ANALYSIS">{tEnum("INTEREST_AREA", "PLAYER_ANALYSIS")}</option>
                                    <option value="SPORTS_JOURNALISM">{tEnum("INTEREST_AREA", "SPORTS_JOURNALISM")}</option>
                                </select>

                            </div>
                        </div>
                    </FadeIn>

                </div>
            </div>
        </>
    );
}
